import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
} from "@repo/ui";
import { formatLocalDateComponents } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/cron")({
  head: () => opsHead("Cron Jobs"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { data } = useSuspenseQuery(
    graphql(`
      query AdminPlatformCron {
        crons {
          id
          lastSuccessAt
        }
      }
    `),
  );

  const [triggerCron, { loading: isTriggeringCron }] = useMutation(
    graphql(`
      mutation AdminPlatformTriggerCron($input: TriggerCronInput!) {
        triggerCron(input: $input) {
          jobId
          taskId
        }
      }
    `),
  );

  const cronItems = [...(data.crons ?? [])].sort((a, b) =>
    a.id.localeCompare(b.id),
  );

  const handleTriggerCron = async (taskId: string) => {
    try {
      const response = await triggerCron({
        variables: {
          input: {
            taskId,
          },
        },
      });
      const payload = response.data?.triggerCron;
      if (!payload) {
        throw new Error("expected triggerCron payload");
      }

      toast({
        title: `Cron ${payload.taskId} has been triggered.`,
        description: `Job ID: ${payload.jobId}`,
        kind: "success",
        autoHide: true,
      });
    } catch (error) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Failed to trigger cron",
        description: String(error),
      });
    }
  };

  return (
    <AdminPageContainer testId="admin-platform-cron">
      <AdminPageHeader title="Cron" subtitle="Do things periodically!" />

      <Command
        filter={(value: string, search: string) => {
          const normalizedValue = value.toLowerCase();
          let score = 0;
          for (const w of search.toLowerCase().split(" ")) {
            if (normalizedValue.includes(w)) {
              score++;
            }
          }
          return score;
        }}
      >
        <CommandInput className="text-lg" autoFocus placeholder="Search..." />
        <CommandList className="max-h-[80vh]">
          <CommandEmpty>No results found.</CommandEmpty>
          {cronItems.map((cronItem) => {
            const lastSuccessComponents = cronItem.lastSuccessAt
              ? formatLocalDateComponents(cronItem.lastSuccessAt)
              : undefined;

            return (
              <CommandItem
                key={cronItem.id}
                value={cronItem.id}
                disabled={isTriggeringCron}
                onSelect={() => {
                  void handleTriggerCron(cronItem.id);
                }}
              >
                <div className="flex w-full cursor-pointer items-center justify-between px-2 py-1">
                  <div className="flex items-center gap-4">
                    <Txt variant="mono">{cronItem.id}</Txt>
                    <Txt variant="detail-label">
                      {lastSuccessComponents
                        ? `Last success at ${lastSuccessComponents.shortDate} ${lastSuccessComponents.shortTime}`
                        : "No invocations recorded"}
                    </Txt>
                  </div>
                  <Button
                    disabled={isTriggeringCron}
                    onMouseDown={(event) => {
                      // because we have onSelect on the row
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      // because we have onSelect on the row
                      event.preventDefault();
                      event.stopPropagation();
                      void handleTriggerCron(cronItem.id);
                    }}
                  >
                    Run
                  </Button>
                </div>
              </CommandItem>
            );
          })}
        </CommandList>
      </Command>
    </AdminPageContainer>
  );
}
